import { Injectable, inject } from '@angular/core';
import { TranslationService } from './translation.service';
import { AppUpdateService } from './app-update.service';
import { MigrationProgressToastService } from './migration-progress-toast.service';
import { TabSyncCoordinatorService } from './tab-sync-coordinator.service';
import { RUNTIME_KIND } from '../tokens/infrastructure.tokens';
import { CapacitorOAuthHandlerService } from './capacitor-oauth-handler.service';

/**
 * Composite bootstrap for the app-shell / presentation layer.
 *
 * Initialization order is fixed and intentional:
 *   1. TranslationService  – must be registered before any service that
 *      produces translated UI output (e.g. toast messages from migration and tab sync coordinators)
 *   2. MigrationProgressToastService – subscribes to migration progress and emits translated toasts
 *   3. AppUpdateService    – PWA update listeners, no ordering dependency
 *   4. TabSyncCoordinatorService – registers BroadcastChannel handlers that
 *      emit translated notifications, so translations must be ready first
 *   5. CapacitorOAuthHandlerService – registers appUrlOpen listener (Capacitor only)
 */
@Injectable({ providedIn: 'root' })
export class AppShellBootstrapService {
  private readonly translationService = inject(TranslationService);
  private readonly migrationProgressToastService = inject(MigrationProgressToastService);
  private readonly appUpdateService = inject(AppUpdateService);
  private readonly tabSyncCoordinatorService = inject(TabSyncCoordinatorService);
  private readonly runtimeKind = inject(RUNTIME_KIND);
  private readonly capacitorOAuthHandler = inject(CapacitorOAuthHandlerService);

  initialize(): void {
    this.translationService.initialize();
    this.migrationProgressToastService.initialize();
    this.appUpdateService.initialize();
    this.tabSyncCoordinatorService.initialize();

    if (this.runtimeKind === 'capacitor') {
      this.capacitorOAuthHandler.initialize();
    }
  }
}
